import { BellRing, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { StatusBadge, Text, UnstyledButton } from '@/shared/ui';
import { Switch } from '@/shared/ui/components/shadcn/switch';
import { frequencyLabel } from '../constants/alert-frequency';
import type { Alert } from '../types/alert.types';

/** One labelled attribute inside the alert card (frequency / target). */
function AlertAttribute({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-(--space-0-5)">
      <Text variant="micro" tone="tertiary" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text variant="meta" fw={600} className="truncate">
        {value}
      </Text>
    </div>
  );
}

interface AlertRowProps {
  alert: Alert;
  onToggle: (alertId: string, next: 'active' | 'inactive') => void;
  onDelete: (alertId: string) => void;
  disabled?: boolean;
}

/**
 * A single recurring-alert card on the list page: title + the icon tile, the
 * frequency/target attributes, the active/inactive status, and the on/off
 * switch plus a delete affordance. The switch persists via the real API
 * (AC-ALERT-3); it is not cosmetic-only state.
 *
 * @param props - The alert plus toggle/delete callbacks and a disabled flag.
 * @returns The alert card element.
 */
export function AlertRow({ alert, onToggle, onDelete, disabled }: AlertRowProps) {
  const active = alert.status === 'active';
  const { t } = useTranslation('features/assistant');
  const statusLabel = active ? t('alerts.status.active') : t('alerts.status.paused');
  return (
    <div
      className="flex w-full flex-col gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) p-(--space-4) shadow-[var(--shadow-sm)]"
      data-testid="alert-row"
    >
      <div className="flex items-center gap-(--space-3)">
        <Text
          component="span"
          className="flex size-9 shrink-0 items-center justify-center rounded-(--radius-md) bg-(--selection-selected) text-(--seed-base)"
          aria-hidden
        >
          <BellRing width={18} height={18} />
        </Text>
        <Text variant="bodySm" fw={700} className="min-w-0 flex-1 truncate">
          {alert.title}
        </Text>
        <StatusBadge tone={active ? 'success' : 'neutral'}>
          {statusLabel}
        </StatusBadge>
      </div>

      <div className="grid grid-cols-1 gap-(--space-2) rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-secondary) p-(--space-3) sm:grid-cols-2 sm:gap-(--space-4)">
        <AlertAttribute label={t('alerts.attributes.frequency')} value={frequencyLabel(alert.frequency)} />
        <AlertAttribute label={t('alerts.attributes.target')} value={alert.target} />
      </div>

      <div className="flex items-center justify-between gap-(--space-3)">
        <div className="flex items-center gap-(--space-2)">
          <Switch
            checked={active}
            disabled={disabled}
            onChange={(next: boolean) => onToggle(alert.alert_id, next ? 'active' : 'inactive')}
            aria-label={t('alerts.toggleAria')}
          />
          <Text variant="meta" fw={600} tone={active ? 'primary' : 'tertiary'}>
            {statusLabel}
          </Text>
        </div>
        <UnstyledButton
          type="button"
          disabled={disabled}
          onClick={() => onDelete(alert.alert_id)}
          aria-label={t('alerts.deleteAria')}
          className="inline-flex cursor-pointer items-center gap-(--space-1) rounded-(--radius-sm) px-(--space-2) py-(--space-0-5) text-(--text-secondary) transition-colors hover:bg-(--surface-hover)"
        >
          <Trash2 width={13} height={13} />
          <Text variant="meta" fw={600} component="span">
            {t('alerts.delete')}
          </Text>
        </UnstyledButton>
      </div>
    </div>
  );
}

export default AlertRow;
